import {Directive, ElementRef, Input, NgZone, OnChanges, SimpleChanges} from '@angular/core';
import {canTakeFocus, focusAtEnd, isShown} from './autofocus-core';

/**
 * Hands the caret back to a page's search box when one of its filters closes, so
 * typing carries on without a click.
 *
 *   <button class="filters-btn" [appReturnSearchFocus]="filtersOpen" …>
 *   <div class="f-field fdd" [appReturnSearchFocus]="statusOpen" …>
 *
 * Bind the filter's OPEN flag; the directive acts on its true → false edge, so
 * every way a filter closes — its toggle again, picking an option, its ✕, an
 * outside click, Escape — is covered by the one line in the template.
 * Components that own their open state (`app-date-range-filter`,
 * `app-product-filter-select`) call `returnFocusToSearch()` from it directly.
 *
 * Why it was needed: `SearchAutofocusDirective` focuses the search ONCE, on
 * arrival. Opening a filter moves focus to its toggle; closing it then either
 * leaves focus on that button or — when the focused option/menu is destroyed by
 * its `@if`, or hidden with the panel — drops it to <body>. Nothing gave it back.
 *
 * "The search" is the nearest `appSearchAutofocus="page"` input (or a field marked
 * `data-filter-search`, for a search that does not autofocus on arrival) walking
 * up from the filter — the search of the same toolbar, not one elsewhere on the
 * page.
 *
 * It never takes focus away from the user:
 *   - a close the user did not cause (a reload, a modal opening) is ignored — only
 *     a pointer or key interaction in the last second counts;
 *   - a close caused by a click on ANOTHER control (the next dropdown, a row link,
 *     a tab) leaves focus to that control — only clicks inside the filter itself,
 *     on empty page, or Escape send it back;
 *   - the shared `canTakeFocus` rules apply (not over another text field, not
 *     behind a modal, not into a hidden field);
 *   - focus the user has since moved elsewhere (Tab) is left there.
 * Several filters closing in one interaction (the Filters toggle hiding a panel
 * with a menu open) focus once. `preventScroll` keeps the page where it is.
 */
@Directive({
  selector: '[appReturnSearchFocus]',
  standalone: true,
})
export class ReturnSearchFocusDirective implements OnChanges {

  @Input('appReturnSearchFocus') open: boolean | null | undefined = false;

  constructor(private el: ElementRef<HTMLElement>, zone: NgZone) {
    // A capture listener per pointerdown/keydown must not wake change detection.
    zone.runOutsideAngular(() => trackFilterInteractions());
  }

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['open'];
    if (change && !change.firstChange && change.previousValue && !change.currentValue) {
      returnFocusToSearch(this.el.nativeElement);
    }
  }
}

const SEARCH_SELECTOR = 'input[appSearchAutofocus="page"], input[data-filter-search]';

/** What a click "on another control" means — that control keeps the focus. */
const INTERACTIVE = 'a[href], button, input, select, textarea, label, summary, [contenteditable="true"], '
  + '[role="button"], [role="tab"], [role="link"], [role="menuitem"], [role="option"], [tabindex]:not([tabindex="-1"])';

/** A close later than this after the last interaction was not the user's doing. */
const INTERACTION_WINDOW_MS = 1000;

let lastInteraction: { target: Element | null; escape: boolean; at: number } | null = null;
let tracking = false;
let pending: { search: HTMLInputElement; owners: Element[] } | null = null;
let timer: ReturnType<typeof setTimeout> | undefined;

/** Records the last pointer/key interaction (capture phase, before any handler stops it). */
export function trackFilterInteractions(): void {
  if (tracking || typeof document === 'undefined') {
    return;
  }
  tracking = true;
  const note = (event: Event) => {
    lastInteraction = {
      target: event.target instanceof Element ? event.target : null,
      escape: event instanceof KeyboardEvent && event.key === 'Escape',
      at: Date.now(),
    };
  };
  document.addEventListener('pointerdown', note, true);
  document.addEventListener('keydown', note, true);
}

/**
 * A filter rooted at `host` just closed: put the caret back in its search once the
 * close has rendered. `owned` are parts of the filter that live outside `host`
 * (a popover portaled to <body>).
 */
export function returnFocusToSearch(host: HTMLElement, ...owned: (Element | null | undefined)[]): void {
  const owners = [host, ...owned].filter((el): el is Element => !!el);
  const search = searchFor(host);
  if (!search || !closedByUser(owners, search)) {
    return;
  }
  pending = {search, owners};
  // After change detection has hidden/destroyed the menu — and once per interaction.
  timer ??= setTimeout(() => {
    timer = undefined;
    const next = pending;
    pending = null;
    if (next) {
      restore(next.search, next.owners);
    }
  });
}

/** The nearest page search around the filter — null when it has none on screen. */
function searchFor(host: HTMLElement): HTMLInputElement | null {
  for (let el = host.parentElement; el; el = el.parentElement) {
    const found = Array.from(el.querySelectorAll<HTMLInputElement>(SEARCH_SELECTOR));
    if (found.length) {
      return found.find(isShown) ?? null;
    }
  }
  return null;
}

function closedByUser(owners: Element[], search: HTMLElement): boolean {
  const last = lastInteraction;
  if (!last || Date.now() - last.at > INTERACTION_WINDOW_MS) {
    return false;
  }
  if (last.escape) {
    return true;
  }
  const target = last.target;
  if (!target || search.contains(target) || owners.some(owner => owner.contains(target))) {
    return true;
  }
  return !target.closest(INTERACTIVE);
}

function restore(search: HTMLInputElement, owners: Element[]): void {
  const active = document.activeElement;
  if (active === search || !canTakeFocus(search, active)) {
    return;
  }
  // Focus still on the filter (its toggle), or lost with the closed menu → ours.
  // Anywhere else, the user has put it there.
  const lost = !active || active === document.body || !(active instanceof HTMLElement) || !isShown(active);
  if (!lost && !owners.some(owner => owner.contains(active))) {
    return;
  }
  focusAtEnd(search, true);
}
